export class ApiError extends Error {
  public data: Record<string, unknown> | null;
  public status: number;

  constructor(
    status: number,
    message: string,
    data?: Record<string, unknown> | null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data ?? null;
  }
}

export const STAFF_SESSION_EXPIRED_EVENT =
  "evolys:staff-session-expired";

const RETRY_STATUS_CODES = [502, 503, 504];
const RETRY_DELAY_MS = 1000;
const SLOW_REQUEST_THRESHOLD_MS = 500;

function isRetryableMethod(method: string): boolean {
  return method === "GET";
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function notifyStaffSessionExpired(status: number): void {
  if (status !== 401 || typeof window === "undefined") return;
  window.dispatchEvent(new Event(STAFF_SESSION_EXPIRED_EVENT));
}

function isAnonymousSecureResultLink(path: string): boolean {
  return path.startsWith("/api/portal/result-links/");
}

function isJsonContentType(contentType: string | null): boolean {
  const mediaType = contentType?.split(";", 1)[0]?.trim().toLowerCase();
  return (
    mediaType === "application/json" ||
    mediaType?.endsWith("+json") === true
  );
}

async function readErrorData(
  response: Response,
): Promise<Record<string, unknown> | null> {
  if (!isJsonContentType(response.headers.get("content-type"))) {
    return null;
  }

  try {
    const value: unknown = await response.json();
    return value !== null && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function firstValidationError(
  data: Record<string, unknown> | null,
): string | undefined {
  if (!data?.errors || typeof data.errors !== "object") return undefined;

  for (const messages of Object.values(
    data.errors as Record<string, unknown>,
  )) {
    if (Array.isArray(messages)) {
      const message = messages.find(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0,
      );
      if (message) return message;
    } else if (typeof messages === "string" && messages.trim()) {
      return messages;
    }
  }

  return undefined;
}

function apiErrorMessage(
  status: number,
  data: Record<string, unknown> | null,
): string {
  const candidates = [
    data?.message,
    data?.detail,
    firstValidationError(data),
    data?.error,
    data?.title,
  ];
  const message = candidates.find(
    (value): value is string =>
      typeof value === "string" && value.trim().length > 0,
  );
  return message?.trim() ?? `API Error: ${status}`;
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const method = (options.method ?? "GET").toUpperCase();
    const canRetry = isRetryableMethod(method);

    const start = performance.now();
    let response: Response;

    try {
      response = await fetch(url, {
        ...options,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      });
    } catch (error) {
      // Network error — retry GET once
      if (canRetry) {
        await sleep(RETRY_DELAY_MS);
        response = await fetch(url, {
          ...options,
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...options.headers,
          },
        });
      } else {
        throw error;
      }
    }

    // Retry on 502/503/504 for GET requests
    if (canRetry && RETRY_STATUS_CODES.includes(response.status)) {
      await sleep(RETRY_DELAY_MS);
      response = await fetch(url, {
        ...options,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      });
    }

    const duration = performance.now() - start;
    if (duration > SLOW_REQUEST_THRESHOLD_MS) {
      console.warn(
        `[ApiClient] Slow request: ${method} ${endpoint} took ${Math.round(duration)}ms`,
      );
    }

    if (!response.ok) {
      notifyStaffSessionExpired(response.status);
      const errorData = await readErrorData(response);
      const message = apiErrorMessage(response.status, errorData);
      throw new ApiError(response.status, message, errorData);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  sendBeacon(endpoint: string): boolean {
    if (typeof navigator === "undefined" || !navigator.sendBeacon) {
      return false;
    }

    return navigator.sendBeacon(`${this.baseUrl}${endpoint}`);
  }

  async getBlob(path: string): Promise<Blob> {
    const start = performance.now();
    let response: Response;

    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        method: "GET",
        credentials: "include",
      });
    } catch (error) {
      // Retry once on network error
      await sleep(RETRY_DELAY_MS);
      response = await fetch(`${this.baseUrl}${path}`, {
        method: "GET",
        credentials: "include",
      });
    }

    // Retry on 502/503/504
    if (RETRY_STATUS_CODES.includes(response.status)) {
      await sleep(RETRY_DELAY_MS);
      response = await fetch(`${this.baseUrl}${path}`, {
        method: "GET",
        credentials: "include",
      });
    }

    const duration = performance.now() - start;
    if (duration > SLOW_REQUEST_THRESHOLD_MS) {
      console.warn(
        `[ApiClient] Slow request: GET ${path} took ${Math.round(duration)}ms`,
      );
    }

    if (!response.ok) {
      notifyStaffSessionExpired(response.status);
      throw new ApiError(response.status, `API Error: ${response.status}`);
    }
    return response.blob();
  }

  async postBlob(path: string, data?: unknown): Promise<Blob> {
    const start = performance.now();
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: data ? JSON.stringify(data) : undefined,
    });

    const duration = performance.now() - start;
    if (duration > SLOW_REQUEST_THRESHOLD_MS) {
      console.warn(
        `[ApiClient] Slow request: POST ${path} took ${Math.round(duration)}ms`,
      );
    }

    if (!response.ok) {
      if (!isAnonymousSecureResultLink(path)) {
        notifyStaffSessionExpired(response.status);
      }
      const errorData = await readErrorData(response);
      const message = apiErrorMessage(response.status, errorData);
      throw new ApiError(response.status, message, errorData);
    }
    return response.blob();
  }

  async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const start = performance.now();

    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
      // Do NOT set Content-Type — browser sets multipart boundary automatically
    });

    const duration = performance.now() - start;
    if (duration > SLOW_REQUEST_THRESHOLD_MS) {
      console.warn(
        `[ApiClient] Slow request: POST ${endpoint} took ${Math.round(duration)}ms`,
      );
    }

    if (!response.ok) {
      notifyStaffSessionExpired(response.status);
      const errorData = await readErrorData(response);
      const message = apiErrorMessage(response.status, errorData);
      throw new ApiError(response.status, message, errorData);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }
}
